import React, {useEffect, useRef, useState} from 'react';
import { Link } from 'react-router-dom';
import Button from '../Button/Button';
import Modal from '../../../modal/modal';
import ModalContent from '../../../modal/ModalContent';
import ModalUserDelete from '../../modalContent/userDelete/userDelete';
import axiosInstance from '../../../utils/router/axiosInstance';
import { logger } from '../../../utils/logger';

// WEB-7: форма редактирования карточки + должность — за build-флагом
// (дефолт off = read-only профиль байт-в-байт, конвенция WEB-3/4/5)
const EMPLOYEE_CARD_ENABLED = process.env.REACT_APP_EMPLOYEE_CARD === 'true';

const Profile = ({ employeeData, setIsModalActive, openShifts, loading, onEmployeeUpdate }) => {
    const [isModalDeleteActive, setIsModalDeleteActive] = useState(false);
    // WEB-7: режим редактирования карточки
    const [isEditing, setIsEditing] = useState(false);
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    // Гард поздних ответов: PATCH, стартовавший на одном сотруднике, не должен
    // перезаписывать карточку другого после навигации (урок WEB-4/5)
    const currentIdRef = useRef(employeeData.user_id);
    currentIdRef.current = employeeData.user_id;
    // Сессия формы (ревью-воркфлоу): поздний успех PATCH из ПРЕЖНЕГО
    // редактирования не должен закрывать заново открытую форму (терялся ввод)
    const editSessionRef = useRef(0);
    // Гард alert после ухода со страницы (грабля WEB-5); взводится в эффекте —
    // StrictMode-safe
    const aliveRef = useRef(true);
    useEffect(() => {
        aliveRef.current = true;
        return () => {
            aliveRef.current = false;
        };
    }, []);

    // смена сотрудника закрывает форму (значения принадлежали прежнему)
    useEffect(() => {
        setIsEditing(false);
        setForm(null);
    }, [employeeData.user_id]);

    const DeleteActive = () => {
        setIsModalDeleteActive(true)
    };
    const CloseShifts = async (n) => {
        const url = 'mobile/user_end_work';
        const requestBody = {
            user_id: employeeData.user_id,
            shift_docname: '',
            dots: []
        };

        for (let i = 0; i < n; i++) {
            try {
                const response = await axiosInstance.post(url, requestBody);
                logger.debug(`Request ${i + 1} successful:`, response.data);
            } catch (error) {
                logger.error(`Request ${i + 1} failed:`, error);
            }
        }
    };

    const startEdit = () => {
        editSessionRef.current += 1;
        setForm({
            fio: employeeData.fio ?? '',
            age: employeeData.age ?? '',
            city: employeeData.city ?? '',
            address: employeeData.address ?? '',
            position: employeeData.position ?? '',
        });
        setIsEditing(true);
    };

    const handleFormChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const saveCard = async () => {
        if (saving) return;
        const targetId = employeeData.user_id;
        const session = editSessionRef.current;
        // PATCH — частичный: шлём ТОЛЬКО изменённые поля (ревью-воркфлоу:
        // отправка всех полей блокировала правку одной должности у сотрудника
        // с унаследованным от мобильной регистрации age вне 0..150 и плодила
        // ложные записи аудита о «правке» нетронутых полей)
        const initial = {
            fio: String(employeeData.fio ?? ''),
            age: String(employeeData.age ?? ''),
            city: String(employeeData.city ?? ''),
            address: String(employeeData.address ?? ''),
            position: String(employeeData.position ?? ''),
        };
        const changes = {};
        const fio = String(form.fio).trim();
        if (fio !== initial.fio) {
            if (!fio) {
                alert('ФИО не может быть пустым.');
                return;
            }
            changes.fio = fio;
        }
        const ageRaw = String(form.age).trim();
        if (ageRaw !== initial.age) {
            const age = Number(ageRaw);
            // пустая строка Number('')===0 — НЕ «возраст 0» (ревью-воркфлоу,
            // Medium: стёртое поле тихо сохранялось нулём)
            if (ageRaw === '' || !Number.isInteger(age) || age < 0 || age > 150) {
                alert('Возраст: целое число от 0 до 150.');
                return;
            }
            changes.age = age;
        }
        const city = String(form.city).trim();
        if (city !== initial.city) changes.city = city;
        const address = String(form.address).trim();
        if (address !== initial.address) changes.address = address;
        const position = String(form.position).trim();
        if (position !== initial.position) changes.position = position || null; // пусто = сброс
        if (Object.keys(changes).length === 0) {
            alert('Нет изменений.');
            return;
        }
        setSaving(true);
        try {
            const { data } = await axiosInstance.patch(`website/employee/${targetId}`, changes);
            if (!aliveRef.current) return; // страница сотрудника уже покинута
            if (currentIdRef.current !== targetId) return; // уже другой сотрудник
            if (onEmployeeUpdate) onEmployeeUpdate(data);
            // закрыть можно только СВОЮ сессию формы: пользователь мог отменить
            // и открыть форму заново, пока PATCH был в полёте
            if (editSessionRef.current === session) setIsEditing(false);
        } catch (error) {
            logger.error('Error updating employee card:', error);
            if (!aliveRef.current) return; // alert не всплывает на чужой странице
            if (currentIdRef.current === targetId && editSessionRef.current === session) {
                alert('Не удалось сохранить карточку.');
            }
        } finally {
            setSaving(false);
        }
    };

    if (EMPLOYEE_CARD_ENABLED && isEditing && form) {
        return (
            <div className='Profile'>
                <p>Редактирование карточки</p>
                <label className='profileEditLabel'>
                    ФИО:
                    <input className='profileEditInput' type='text' name='fio' value={form.fio} onChange={handleFormChange} />
                </label>
                <label className='profileEditLabel'>
                    Возраст:
                    <input className='profileEditInput' type='number' name='age' value={form.age} onChange={handleFormChange} />
                </label>
                <label className='profileEditLabel'>
                    Город:
                    <input className='profileEditInput' type='text' name='city' value={form.city} onChange={handleFormChange} />
                </label>
                <label className='profileEditLabel'>
                    Адрес:
                    <input className='profileEditInput' type='text' name='address' value={form.address} onChange={handleFormChange} />
                </label>
                <label className='profileEditLabel'>
                    Должность:
                    <input className='profileEditInput' type='text' name='position' value={form.position} onChange={handleFormChange} />
                </label>
                <div className='buttons'>
                    <Button className='btn' text={saving ? 'Сохранение…' : 'Сохранить'} onClick={saveCard} />
                    <Button className='btn' text={'Отмена'} onClick={() => setIsEditing(false)} />
                </div>
            </div>
        );
    }

    return (
        <div className='Profile'>
            <p>Профиль</p>
            <p>Имя: {employeeData.fio}</p>
            <p>Возраст: {employeeData.age}</p>
            <p>Город: {employeeData.city}</p>
            <p>Адрес: {employeeData.address}</p>
            {EMPLOYEE_CARD_ENABLED && (
                <p>Должность: {employeeData.position || '—'}</p>
            )}
            <p>ID: {employeeData.user_id}</p>
            <div className='buttons'>
                <Button className="btn" text={'Статистика'} onClick={() => setIsModalActive(true)} />
                <Link to={`/chat/${employeeData.user_id}`}><Button className="btn" text={'Написать'}/></Link>
            </div>
            <div className='buttons'>
            <Button className={"btn "} text={'Удалить'} onClick={() =>DeleteActive()}/>
            <Button className={"btn"} text={loading ? 'Загрузка...' : 'Закрыть смены'} onClick={() =>CloseShifts(openShifts)}></Button>

            </div>
            {EMPLOYEE_CARD_ENABLED && (
                <div className='buttons'>
                    <Button className='btn' text={'Редактировать'} onClick={startEdit} />
                </div>
            )}
            <Modal active={isModalDeleteActive} setActive={setIsModalDeleteActive} width={"420px"} height={"600px"}>
                <ModalContent><ModalUserDelete userId={employeeData.user_id}/></ModalContent>
            </Modal>

        </div>
    );
};

export default Profile;
